import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, PowerOff, Building2, KeyRound, Copy, CheckCheck, FileText, FileSpreadsheet, X as XIcon, Trash2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";

const empty = {
  name: "", rfc: "", contact_name: "", contact_email: "", contact_phone: "",
  payday_day: 15, payroll_period: "QUINCENAL",
  create_user: true, user_email: "", user_password: "",
  custom_fee_tiers: null,
};

export default function Empresas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  // Default fee tiers come from the backend (single source of truth: see
  // DEFAULT_FEE_TIERS in server.py). We fetch once and reuse to seed the
  // editable "custom tiers" table.
  const [defaultTiers, setDefaultTiers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [credentials, setCredentials] = useState(null); // { company, user: {email, password} }
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { company, impact }
  const [credsView, setCredsView] = useState(null); // { company, user_email, new_password? }
  const [credsBusy, setCredsBusy] = useState(false);
  const [credsCopied, setCredsCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/config/fee-tiers");
        setDefaultTiers(data.tiers || []);
      } catch { /* keep empty; user can add tiers manually */ }
    })();
  }, [isAdmin]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, create_user: false, user_email: "", user_password: "" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const base = {
        name: form.name, rfc: form.rfc, contact_name: form.contact_name,
        contact_email: form.contact_email, contact_phone: form.contact_phone,
        payday_day: Number(form.payday_day),
        payroll_period: form.payroll_period,
        custom_fee_tiers: form.custom_fee_tiers,
      };
      if (editing) {
        await api.put(`/companies/${editing.id}`, base);
        toast.success("Empresa actualizada");
      } else {
        const payload = { ...base, create_user: form.create_user };
        if (form.user_email) payload.user_email = form.user_email;
        if (form.user_password) payload.user_password = form.user_password;
        const res = await api.post("/companies", payload);
        toast.success("Empresa creada");
        if (res.data?.user) {
          setCredentials({ company: res.data.company, user: res.data.user });
        }
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyCreds = () => {
    if (!credentials) return;
    const text = `Portal NomiDiem - Acceso Empresa\nEmpresa: ${credentials.company.name}\nEmail: ${credentials.user.email}\nContraseña: ${credentials.user.password}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCompanyPdf = async (c) => {
    try {
      const token = localStorage.getItem("nomiflex_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/companies/${c.id}/liquidation.pdf`;
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo generar el PDF");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `liquidacion_${c.rfc}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Comprobante descargado");
    } catch (e) {
      toast.error(e.message || "Error al descargar");
    }
  };

  const downloadPendingXlsx = async (c) => {
    try {
      const token = localStorage.getItem("nomiflex_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/companies/${c.id}/pending.xlsx`;
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo exportar");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pendientes_${c.rfc}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Pendientes exportados");
    } catch (e) {
      toast.error(e.message || "Error al exportar");
    }
  };

  // Fee tiers editing helpers (operate on form.custom_fee_tiers)
  const getTiers = () => form.custom_fee_tiers || null;
  const setTiers = (tiers) => setForm({ ...form, custom_fee_tiers: tiers });
  const enableCustomTiers = () => setTiers(defaultTiers.map((t) => ({ ...t })));
  const disableCustomTiers = () => setTiers(null);
  const updateTier = (i, field, value) => {
    const next = [...(getTiers() || [])];
    next[i] = { ...next[i], [field]: Number(value) || 0 };
    setTiers(next);
  };
  const addTier = () => {
    const cur = getTiers() || [];
    const last = cur[cur.length - 1];
    setTiers([...cur, { max_amount: (last?.max_amount || 0) + 5000, fee: (last?.fee || 0) + 10 }]);
  };
  const removeTier = (i) => {
    const next = (getTiers() || []).filter((_, idx) => idx !== i);
    setTiers(next);
  };

  const toggleActive = async (c) => {
    try {
      if (c.active) {
        await api.delete(`/companies/${c.id}`);
        toast.success("Empresa desactivada");
      } else {
        await api.put(`/companies/${c.id}`, { active: true });
        toast.success("Empresa reactivada");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const askDelete = async (c) => {
    try {
      const { data } = await api.get(`/companies/${c.id}/delete-impact`);
      setDeleteTarget({ company: c, impact: data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/companies/${deleteTarget.company.id}/permanent`);
      toast.success("Empresa eliminada");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openCredentials = async (c) => {
    try {
      const { data } = await api.get(`/companies/${c.id}/credentials`);
      setCredsView({ company: c, ...data, new_password: null });
      setCredsCopied(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetCompanyPassword = async () => {
    if (!credsView?.company) return;
    setCredsBusy(true);
    try {
      const { data } = await api.post(`/companies/${credsView.company.id}/reset-password`);
      setCredsView((cv) => ({ ...cv, user_email: data.user_email, new_password: data.new_password }));
      setCredsCopied(false);
      toast.success("Contraseña restablecida");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCredsBusy(false);
    }
  };

  const copyCredsLine = async () => {
    if (!credsView) return;
    const text = credsView.new_password
      ? `Email: ${credsView.user_email}\nContraseña: ${credsView.new_password}`
      : `Email: ${credsView.user_email}`;
    try {
      await navigator.clipboard.writeText(text);
      setCredsCopied(true);
      setTimeout(() => setCredsCopied(false), 2000);
    } catch (_) {
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  const filtered = list.filter(
    (c) =>
      !q ||
      c.name.toLowerCase().includes(q.toLowerCase()) ||
      c.rfc.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="empresas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Empresas</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestión de empresas cliente y sus accesos.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-company-btn" onClick={openCreate} className="bg-[#1D9E75] hover:bg-[#147D5C]">
                <Plus className="w-4 h-4 mr-1.5" />Nueva empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre legal" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="company-name" required />
                <Field label="RFC" value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v.toUpperCase() })} testId="company-rfc" required />
                <Field label="Contacto RRHH" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testId="company-contact" required />
                <Field label="Correo RRHH" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testId="company-email" required />
                <Field label="Teléfono" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testId="company-phone" />
                <Field label="Día de pago (1-31)" type="number" value={form.payday_day} onChange={(v) => setForm({ ...form, payday_day: v })} testId="company-payday" required />
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Periodo de nómina</Label>
                  <Select value={form.payroll_period} onValueChange={(v) => setForm({ ...form, payroll_period: v })}>
                    <SelectTrigger data-testid="company-payroll-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEMANAL">Semanal (cada 7 días)</SelectItem>
                      <SelectItem value="QUINCENAL">Quincenal (cada 15 días)</SelectItem>
                      <SelectItem value="MENSUAL">Mensual (cada 30 días)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom fee tiers */}
                <div className="md:col-span-2 mt-2 pt-3 border-t border-[#E2E8F0]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">Fee personalizado</div>
                      <div className="text-xs text-[#64748B]">Por defecto usa tarifa escalonada estándar. Actívalo para negociar tarifas por volumen.</div>
                    </div>
                    <input
                      type="checkbox"
                      data-testid="company-custom-fee-toggle"
                      checked={!!form.custom_fee_tiers}
                      onChange={(e) => (e.target.checked ? enableCustomTiers() : disableCustomTiers())}
                      className="w-4 h-4 accent-[#1D9E75]"
                    />
                  </div>
                  {form.custom_fee_tiers && (
                    <div className="mt-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 space-y-2" data-testid="fee-tiers-editor">
                      <div className="grid grid-cols-[1fr_1fr_32px] gap-2 text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-1">
                        <div>Hasta (monto MXN)</div>
                        <div>Fee (MXN)</div>
                        <div></div>
                      </div>
                      {form.custom_fee_tiers.map((t, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
                          <Input
                            data-testid={`tier-max-${i}`}
                            type="number" min="1" step="1"
                            value={t.max_amount}
                            onChange={(e) => updateTier(i, "max_amount", e.target.value)}
                            className="h-9"
                          />
                          <Input
                            data-testid={`tier-fee-${i}`}
                            type="number" min="0" step="0.01"
                            value={t.fee}
                            onChange={(e) => updateTier(i, "fee", e.target.value)}
                            className="h-9"
                          />
                          <Button
                            type="button"
                            variant="ghost" size="sm"
                            data-testid={`tier-remove-${i}`}
                            onClick={() => removeTier(i)}
                            className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                          >
                            <XIcon className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline" size="sm"
                        data-testid="tier-add"
                        onClick={addTier}
                        className="w-full mt-1 h-8"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />Agregar escalón
                      </Button>
                      <div className="text-[11px] text-[#64748B] px-1 pt-1 leading-relaxed">
                        Los montos solicitados ≤ "Hasta" cobran el "Fee" correspondiente. Si el monto excede el último escalón, se aplica el fee del último.
                      </div>
                    </div>
                  )}
                </div>
                {!editing && (
                  <>
                    <div className="md:col-span-2 mt-2 pt-3 border-t border-[#E2E8F0]">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">Crear acceso de usuario empresa</div>
                          <div className="text-xs text-[#64748B]">El director de RRHH podrá iniciar sesión con estas credenciales</div>
                        </div>
                        <input
                          type="checkbox"
                          data-testid="company-create-user"
                          checked={form.create_user}
                          onChange={(e) => setForm({ ...form, create_user: e.target.checked })}
                          className="w-4 h-4 accent-[#1D9E75]"
                        />
                      </div>
                    </div>
                    {form.create_user && (
                      <>
                        <Field label="Email de acceso" placeholder="(opcional, usa el contacto)" value={form.user_email} onChange={(v) => setForm({ ...form, user_email: v })} testId="company-user-email" />
                        <Field label="Contraseña" placeholder="(opcional, se genera automáticamente)" value={form.user_password} onChange={(v) => setForm({ ...form, user_password: v })} testId="company-user-password" />
                        <div className="md:col-span-2 text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-2.5 -mt-1">
                          <KeyRound className="w-3.5 h-3.5 inline mr-1.5 text-[#1D9E75]" />
                          Si dejas la contraseña vacía, el sistema generará una segura automáticamente y la mostrará después.
                        </div>
                      </>
                    )}
                  </>
                )}
                <DialogFooter className="md:col-span-2 mt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="company-submit-btn" className="bg-[#1D9E75] hover:bg-[#147D5C]">
                    {editing ? "Guardar cambios" : "Crear empresa"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4">
        <Input
          data-testid="empresas-search"
          placeholder="Buscar por nombre o RFC..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
      </Card>

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="p-8 flex justify-center"><div className="nomi-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-10 h-10 text-[#CBD5E1] mx-auto" />
            <div className="mt-3 text-sm text-[#64748B]">No hay empresas registradas</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="empresas-table">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B]">
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">RFC</th>
                  <th className="px-4 py-3 font-medium">Contacto RRHH</th>
                  <th className="px-4 py-3 font-medium">Día pago</th>
                  <th className="px-4 py-3 font-medium">Periodo</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Alta</th>
                  {isAdmin && <th className="px-4 py-3 font-medium text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#334155]">{c.rfc}</td>
                    <td className="px-4 py-3 text-[#475569]">
                      <div>{c.contact_name}</div>
                      <div className="text-xs text-[#64748B]">{c.contact_email}</div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{c.payday_day}</td>
                    <td className="px-4 py-3 text-xs font-semibold">
                      <span className={`px-2 py-0.5 rounded-full ${
                        c.payroll_period === "MENSUAL" ? "bg-indigo-50 text-indigo-700" :
                        c.payroll_period === "SEMANAL" ? "bg-amber-50 text-amber-700" :
                        "bg-[#D1FAE5] text-[#147D5C]"
                      }`}>
                        {c.payroll_period === "MENSUAL" ? "Mensual" : c.payroll_period === "SEMANAL" ? "Semanal" : "Quincenal"}
                      </span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={c.active ? "ACTIVA" : "INACTIVA"} /></td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">{formatDate(c.created_at)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm" variant="outline"
                            data-testid={`xlsx-company-${c.id}`}
                            onClick={() => downloadPendingXlsx(c)}
                            title="Exportar pendientes (Excel)"
                          >
                            <FileSpreadsheet className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            data-testid={`pdf-company-${c.id}`}
                            onClick={() => downloadCompanyPdf(c)}
                            title="Descargar comprobante de liquidación"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`edit-company-${c.id}`} onClick={() => openEdit(c)} title="Editar">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`creds-company-${c.id}`} onClick={() => openCredentials(c)} title="Ver credenciales RRHH">
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`toggle-company-${c.id}`} onClick={() => toggleActive(c)} title={c.active ? "Desactivar" : "Reactivar"}>
                            <PowerOff className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`delete-company-${c.id}`}
                            onClick={() => askDelete(c)}
                            title="Eliminar permanentemente"
                            className="text-[#B91C1C] hover:bg-rose-50 border-rose-200"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Generated credentials dialog */}
      <Dialog open={!!credentials} onOpenChange={(v) => !v && setCredentials(null)}>
        <DialogContent className="max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#D1FAE5] flex items-center justify-center">
                <KeyRound className="w-4 h-4 text-[#147D5C]" />
              </div>
              Acceso generado para la empresa
            </DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4">
              <div className="text-sm text-[#475569] leading-relaxed">
                Comparte estas credenciales con el director de RRHH de <strong>{credentials.company.name}</strong>.
                Esta es la única vez que la contraseña aparecerá en pantalla.
              </div>
              <div className="bg-[#0F172A] text-white rounded-lg p-4 space-y-3 font-mono text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-0.5">Portal</div>
                  <div className="text-[13px]">{window.location.origin}/login</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-0.5">Email</div>
                  <div className="text-[#6EE7B7]" data-testid="gen-email">{credentials.user.email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-0.5">Contraseña</div>
                  <div className="text-[#6EE7B7] text-base tracking-wide" data-testid="gen-password">{credentials.user.password}</div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-xs leading-relaxed">
                <strong>Importante:</strong> El usuario de RRHH solo verá a sus empleados, historial de adelantos del mes y la próxima liquidación. No tiene acceso al dashboard administrativo.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={copyCreds} data-testid="copy-creds-btn">
                  {copied ? <><CheckCheck className="w-4 h-4 mr-1.5" />Copiado</> : <><Copy className="w-4 h-4 mr-1.5" />Copiar credenciales</>}
                </Button>
                <Button onClick={() => setCredentials(null)} data-testid="close-creds-btn" className="bg-[#1D9E75] hover:bg-[#147D5C]">
                  Entendido
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!credsView} onOpenChange={(v) => !v && setCredsView(null)}>
        <DialogContent data-testid="creds-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex w-8 h-8 rounded-full bg-[#D1FAE5] text-[#147D5C] items-center justify-center">
                <KeyRound className="w-4 h-4" />
              </span>
              Credenciales RRHH — {credsView?.company?.name}
            </DialogTitle>
          </DialogHeader>

          {credsView?.has_user === false ? (
            <div className="space-y-3 text-sm">
              <p className="text-[#475569]">Esta empresa todavía no tiene un usuario RRHH configurado.</p>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#64748B]">Correo de acceso</div>
                <div data-testid="creds-email" className="font-mono text-[#0F172A] mt-1 break-all">
                  {credsView?.user_email}
                </div>
              </div>

              {credsView?.new_password ? (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-[#147D5C] font-semibold">
                    Nueva contraseña generada
                  </div>
                  <div data-testid="creds-new-password" className="font-mono text-[#0F172A] text-base break-all select-all">
                    {credsView.new_password}
                  </div>
                  <p className="text-[11px] text-[#147D5C] leading-snug">
                    Copia y comparte esta contraseña ahora. Por seguridad, no podrás verla de nuevo.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-xs text-[#64748B] leading-snug">
                  La contraseña actual se guarda encriptada (bcrypt) y no puede recuperarse.
                  Si el director de RRHH la olvidó, genera una nueva con el botón de abajo.
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="creds-copy-btn"
                  onClick={copyCredsLine}
                  className="gap-1.5"
                >
                  {credsCopied ? <CheckCheck className="w-3.5 h-3.5 text-[#147D5C]" /> : <Copy className="w-3.5 h-3.5" />}
                  {credsCopied ? "Copiado" : "Copiar"}
                </Button>
                <Button
                  type="button"
                  data-testid="creds-reset-btn"
                  onClick={resetCompanyPassword}
                  disabled={credsBusy}
                  className="bg-[#1D9E75] hover:bg-[#147D5C] text-white gap-1.5"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  {credsBusy ? "Generando..." : (credsView?.new_password ? "Restablecer otra vez" : "Restablecer contraseña")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar empresa"
        description={
          deleteTarget && (
            <>
              ¿Estás seguro de que deseas eliminar a{" "}
              <strong>{deleteTarget.company.name}</strong>?
            </>
          )
        }
        warnings={(() => {
          if (!deleteTarget?.impact) return [];
          const w = [];
          const i = deleteTarget.impact;
          if (i.active_employees > 0) {
            w.push(`Tiene ${i.active_employees} empleado(s) activo(s) que también se eliminarán.`);
          }
          if (i.inactive_employees > 0) {
            w.push(`Y ${i.inactive_employees} empleado(s) inactivo(s) en el histórico.`);
          }
          if (i.open_advances > 0) {
            w.push(`Hay ${i.open_advances} adelanto(s) en proceso (no podrás eliminar hasta liquidarlos).`);
          }
          if (i.open_liquidations > 0) {
            w.push(`Hay ${i.open_liquidations} liquidación(es) sin marcar como recibidas.`);
          }
          return w;
        })()}
        confirmLabel="Eliminar empresa"
        busyLabel="Eliminando..."
        onConfirm={confirmDelete}
        testId="confirm-delete-company"
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
      />
    </div>
  );
}
